/* eslint-disable @typescript-eslint/no-empty-interface */
/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable react/no-unstable-nested-components */
/* eslint-disable no-param-reassign */
import React, {
  useRef, useMemo,
  useState,
} from 'react';
import {
  Button, Checkbox, Paper, Switch, Tooltip,
} from '@mui/material';
import { convertApiDataToTable } from 'src/pages/JobRequisition/utils';
import { createColumnHelper } from '@tanstack/react-table';
import { useNavigate } from 'react-router-dom';
import { LinkIcon } from 'src/icons';
import { TbStatusChange } from 'react-icons/tb';
import { confirmWrapper } from 'src/components/Dialog';
import {
  Confirmation, Loader, Popover, SpinnerLoader, Table,
} from 'src/components';
import { PopoverComponentRef } from 'src/components/Popover';
import { useRecruiters } from 'src/hooks/query/recruiters';
import { useClients } from 'src/hooks/query/clients';
import { hasModuleAccess, useAuth } from '@panna/auth';
import { Module } from 'src/access';
import { useDispatch } from 'react-redux';
import { AppDispatch } from 'src/store';
import { updateTab } from 'src/store/jobRequisitionTabs';
import copyTextToClipboard from 'src/pages/ViewJob/utils';
import { useLocations } from 'src/hooks/query/locations';
import {
  JobListTable,
  JobListValuesTypes,
  JobRequisitionViewRendererProps,
  PostOnWebsiteProps,
  ReferenceLinksProps,
} from '../../types';
import ReferenceLinksPopup from '../../components/ReferenceLinksPopup';
import JobListFilters from '../../components/JobListFilters';

function Desktop(props: JobRequisitionViewRendererProps) {
  const {
    jobsFetching,
    loading,
    jobs,
    rowSelection,
    onRowSelectionChange,
    changeMultiStatus,
    showConfigureInterviewPopup,
    showCreateJob,
    postJobOnWebsite,
    shareInternalLink,
    onApplyFilter,
    onFiltersReset,
  } = props;

  const authContext = useAuth();
  const { authState: auth } = authContext;
  const isATSRecruiter = hasModuleAccess(
    auth?.userInfo?.modules || [],
    Module.ATS_RECRUITER,
  );

  const { recruitersOptions } = useRecruiters();
  const { locationsOptions } = useLocations();
  const { clientOptions } = useClients();

  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();

  const [copiedJobId, setCopiedJobId] = useState<boolean>(false);

  const toggleRef = useRef<PopoverComponentRef>(null);

  // mapping from recruiter ID to name
  const recruiterIdToNameMap = useMemo(() => {
    const map: Record<string, string> = {};
    recruitersOptions.forEach((recruiter) => {
      map[String(recruiter.value)] = recruiter.label;
    });
    return map;
  }, [recruitersOptions]);

  // mapping from location ID to name
  const locationIdToNameMap = useMemo(() => {
    const map: Record<string, string> = {};
    locationsOptions.forEach((location) => {
      map[String(location.value)] = location.label;
    });
    return map;
  }, [locationsOptions]);

  // mapping from client ID to name
  const clientIdToNameMap = useMemo(() => {
    const map: Record<string, string> = {};
    clientOptions.forEach((client) => {
      map[client.value] = client.label;
    });
    return map;
  }, [recruitersOptions]);

  const showReferenceLinksPopup = ({
    internalLink,
    externalLink,
    jobOrderId,
    jobTitle,
    location,
    client,
  }: ReferenceLinksProps) => {
    confirmWrapper('', {
      variant: '',
      hidePrimaryBtn: true,
      hideSecondaryBtn: true,
      maxWidth: 'md',
      fullWidth: true,
      containerClasses: 'p-0 px-10',
      showCloseButton: true,
      disableBackdropClickClose: true,
      disableEscapeKeyDownClose: true,
      dialogContent: (
        <ReferenceLinksPopup
          internalLink={internalLink}
          externalLink={externalLink}
          jobOrderId={jobOrderId}
          jobTitle={jobTitle}
          location={location}
          client={client}
          shareInternalLink={shareInternalLink}
        />
      ),
    });
  };

  const handleToggle = async (data: PostOnWebsiteProps) => {
    if (toggleRef.current && toggleRef.current.close) {
      toggleRef.current?.close();
    }
    await postJobOnWebsite(data);
  };

  const copyJobId = (text: string) => {
    copyTextToClipboard(text);
    setCopiedJobId(true);
    setTimeout(() => {
      setCopiedJobId(false);
    }, 1000);
  };

  const onRowClick = (e: JobListValuesTypes) => {
    const { jobId, jobOrderId } = e;
    const newTab = {
      label: jobId,
      to: `/job-requisition/view-job/${jobOrderId}`,
      closeable: true,
      jobId,
      jobOrderId,
    };
    dispatch(updateTab(newTab));
    navigate(`/job-requisition/view-job/${jobOrderId}`);
  };

  const columnHelper = createColumnHelper<JobListTable & { sNo?: number }>();
  const columns = [
    columnHelper.accessor('sNo', {
      header: ({ table }) => (
        <div className="ml-6">
          <Checkbox
            indeterminate={table.getIsSomeRowsSelected()}
            checked={table.getIsAllRowsSelected()}
            onChange={table.getToggleAllRowsSelectedHandler()}
          />
        </div>
      ),
      cell: ({ row }) => (
        <div className="ml-6">
          <Checkbox
            indeterminate={row.getIsSomeSelected()}
            checked={row.getIsSelected()}
            disabled={!row.getCanSelect()}
            onMouseDown={(event) => {
              event.stopPropagation();
            }}
            onClick={(event) => {
              event.stopPropagation();
            }}
            onChange={row.getToggleSelectedHandler()}
          />
        </div>
      ),
    }),
    columnHelper.accessor('jobId', {
      header: () => 'Job Id & Title',
      cell: (info) => (
        <div className="flex flex-col w-[200px]">
          <Tooltip open={copiedJobId} title="Copied" followCursor>
            <div
              className="cursor-pointer hover:bg-[#ddd] w-fit"
              onClick={(e) => {
                e.stopPropagation();
                copyJobId(info.row.original.jobId);
              }}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.stopPropagation();
                  copyJobId(info.row.original.jobId);
                }
              }}
            >
              {info.row.original.jobId}
            </div>
          </Tooltip>
          <Tooltip title={info.row.original.jobTitle}>
            <div className="w-[90%] truncate">{info.row.original.jobTitle}</div>
          </Tooltip>
        </div>
      ),
    }),
    columnHelper.accessor('activeDays', {
      header: () => 'Days',
      cell: (info) => info.getValue(),
    }),
    columnHelper.accessor('location', {
      header: () => 'Job Location',
      cell: (info) => {
        const locationNames = info
          .getValue()
          ?.map((loc: string) => {
            const key = String(loc ?? '').trim();
            if (!key) {
              return '';
            }
            // Backend may already return names; otherwise map id -> name
            if (!/^\d+$/.test(key)) {
              return key;
            }
            return locationIdToNameMap[key] || key;
          })
          .filter(Boolean);
        return (
          <Tooltip placement="bottom-start" title={locationNames?.join(', ')}>
            <div className="w-[200px] pr-2">
              {locationNames?.slice(0, 2)?.map((location, index) => (
                <div className="truncate">
                  {index === 1 && locationNames?.length > 2
                    ? `${location}...`
                    : location}
                </div>
              ))}
            </div>
          </Tooltip>
        );
      },
    }),
    columnHelper.accessor('clientId', {
      header: () => 'Client',
      cell: (info) => (
        <Tooltip placement="bottom" title={clientIdToNameMap[info.getValue()] || info.getValue()}>
          <div className="max-w-[200px] truncate pr-2">
            {clientIdToNameMap[info.getValue()] || info.getValue()}
          </div>
        </Tooltip>
      ),
    }),
    columnHelper.accessor('total', {
      header: () => 'Count',
      cell: (info) => (
        <div className="flex">
          <Tooltip placement="bottom" title="Total">
            <div>{info.getValue()}</div>
          </Tooltip>
          <div>/</div>
          <Tooltip placement="bottom" title="Submitted">
            <div>{info.row.original.submitted}</div>
          </Tooltip>
        </div>
      ),
    }),
    columnHelper.accessor('recruiters', {
      header: () => 'Recruiters',
      cell: (info) => {
        const recruiterNames = info
          .getValue()
          ?.map((id: string) => recruiterIdToNameMap[String(id)] || id);
        return (
          <Tooltip placement="bottom-start" title={recruiterNames?.join(', ')}>
            <div className="w-[200px] pr-2">
              {recruiterNames?.slice(0, 2)?.map((recruiter, index) => (
                <div className="truncate">
                  {index === 1 && recruiterNames.length > 2
                    ? `${recruiter}...`
                    : recruiter}
                </div>
              ))}
            </div>
          </Tooltip>
        );
      },
    }),
    columnHelper.accessor('status', {
      header: () => 'Status',
      cell: (info) => (
        <div className="flex items-start justify-center flex-col gap-1">
          <div>
            {info.getValue().charAt(0).toUpperCase()
                + info.getValue().slice(1).toLowerCase()}
          </div>
        </div>
      )
      ,
    }),
    columnHelper.accessor('postOnWebSite', {
      header: () => '',
      cell: (info) => {
        const [spinnerLoading, setSpinnerLoading] = useState(false);
        return (
          <div className="flex items-center gap-2">
            <Tooltip placement="bottom-start" title="reference links">
              <div
                onClick={(event) => {
                  event.stopPropagation();
                  showReferenceLinksPopup({
                    internalLink: info.row.original.internalLink,
                    externalLink: info.row.original.externalLink,
                    jobOrderId: info.row.original.jobOrderId,
                    jobTitle: info.row.original.jobTitle,
                    location: info.row.original.location,
                    client: clientIdToNameMap[info.row.original.clientId]
                    || info.row.original.clientId.toString(),
                  });
                }}
              >
                <LinkIcon />
              </div>
            </Tooltip>
            <Tooltip
              placement="bottom-start"
              title="configure interview position"
            >
              <div
                onClick={(event) => {
                  event.stopPropagation();
                  showConfigureInterviewPopup({
                    jobOrderId: info.row.original.jobOrderId,
                    interviewPositionId: info.row.original.interviewPositionId,
                  });
                }}
              >
                <TbStatusChange color="#F97171" />
              </div>
            </Tooltip>
            <Popover
              ref={toggleRef}
              Component={(
                <Confirmation
                  variant="warning"
                  text={`Are you sure you want to ${
                    info.getValue() ? 'remove this job from' : 'post this job to'
                  } job board?`}
                  confirm={async () => {
                    setSpinnerLoading(true);
                    await handleToggle({
                      isPosted: info.getValue(),
                      jobOrderId: info.row.original?.jobOrderId,
                    });
                    setSpinnerLoading(false);
                  }}
                />
              )}
            >
              <Tooltip
                placement="bottom-start"
                title={`${
                  info.getValue() ? 'remove from job board' : 'post to job board'
                }`}
              >
                {
                  spinnerLoading
                    ? <SpinnerLoader />
                    : <Switch checked={info.getValue()} />
                }
                {/* <Switch checked={info.getValue()} /> */}

              </Tooltip>
            </Popover>
          </div>
        );
      },
    }),
  ];

  return (
    <div className="p-0 py-2">
      <JobListFilters
        isATSRecruiter={isATSRecruiter}
        onReset={onFiltersReset}
        onSubmit={onApplyFilter}
      />
      <div className="flex gap-4 bg-white p-4 default-shadow-table rounded-md rounded-b-none justify-between items-end flex-row w-full">
        <div>
          <Button
            variant="contained"
            fullWidth
            className="font-medium whitespace-nowrap rounded-lg"
            size="small"
            onClick={() => {
              changeMultiStatus(rowSelection);
            }}
            disabled={Object.keys(rowSelection).length <= 0}
          >
            Change Status
          </Button>
        </div>
        <div>
          <Button
            variant="contained"
            fullWidth
            className="font-medium whitespace-nowrap rounded-lg"
            size="small"
            onClick={showCreateJob}
          >
            Create Job
          </Button>
        </div>
      </div>
      <Paper className="mb-4 flex items-center justify-center default-shadow-table overflow-x-auto">
        <Table
          loading={jobsFetching}
          onRowClick={onRowClick}
          data={convertApiDataToTable(jobs)}
          columns={columns}
          onRowSelection={onRowSelectionChange}
          rowSelection={rowSelection}
          pageSize={20}
        />
        {loading && (
        <div className="min-h-[50vh] flex items-center justify-center absolute">
          <Loader />
        </div>
        )}
      </Paper>
    </div>
  );
}

export default Desktop;
